import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

const QRLogin = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { t, API, token } = useApp();
  
  const [status, setStatus] = useState('checking'); // checking, authenticating, success, error, not-logged-in
  const [error, setError] = useState('');

  useEffect(() => {
    const authenticateSession = async () => {
      // Check if user is logged in on this device
      if (!token) {
        setStatus('not-logged-in');
        return;
      }

      setStatus('authenticating');

      try {
        const response = await fetch(`${API}/auth/qr/authenticate/${sessionId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || t('qr_auth_failed'));
        }

        setStatus('success');
        toast.success(t('qr_login_authorized'));
        
        // Redirect to home after a moment
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } catch (err) {
        setStatus('error');
        setError(err.message);
        toast.error(err.message);
      }
    };

    authenticateSession();
  }, [sessionId, token, API, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'checking':
      case 'authenticating':
        return (
          <div className="text-center">
            <Loader2 size={64} className="mx-auto text-[#D4AF37] animate-spin mb-6" />
            <h2 className="font-heading text-2xl text-white mb-2">
              {t('qr_authorizing')}
            </h2>
            <p className="text-[#A8A29E]">
              {t('qr_please_wait')}
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircle size={64} className="mx-auto text-green-500 mb-6" />
            <h2 className="font-heading text-2xl text-white mb-2">
              {t('qr_login_success')}
            </h2>
            <p className="text-[#A8A29E] mb-6">
              {t('qr_logged_in_other_device')}
            </p>
            <button
              onClick={() => navigate('/home')}
              className="btn-primary"
            >
              {t('back_to_home')}
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <XCircle size={64} className="mx-auto text-red-500 mb-6" />
            <h2 className="font-heading text-2xl text-white mb-2">
              {t('qr_auth_failed_title')}
            </h2>
            <p className="text-[#A8A29E] mb-2">
              {error || t('error_occurred')}
            </p>
            <p className="text-[#57534E] text-sm mb-6">
              {t('qr_expired_or_used')}
            </p>
            <button
              onClick={() => navigate('/home')}
              className="btn-primary"
            >
              {t('back_to_home')}
            </button>
          </div>
        );

      case 'not-logged-in':
        return (
          <div className="text-center">
            <XCircle size={64} className="mx-auto text-[#D4AF37] mb-6" />
            <h2 className="font-heading text-2xl text-white mb-2">
              {t('not_logged_in')}
            </h2>
            <p className="text-[#A8A29E] mb-6">
              {t('qr_must_be_logged_in')}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/login?redirect=/qr-login/${sessionId}`)}
                className="btn-primary w-full"
              >
                {t('login')}
              </button>
              <button
                onClick={() => navigate('/home')}
                className="btn-secondary w-full"
              >
                {t('back_to_home')}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12 relative grain"
      style={{ background: 'radial-gradient(circle at center, #2C1B18 0%, #050505 100%)' }}
    >
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl">
            <span className="gold-text">Zuiver</span>
            <span className="text-white">Parfum</span>
          </h1>
        </div>

        {/* Content Card */}
        <div className="glass p-8 rounded-2xl">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default QRLogin;
